import React, { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';

import debounce from 'lodash/debounce';

import { Autocomplete, Box, Stack, TextField, Typography } from '@mui/material';

import { CommissionOption, RuleCondition, ensureAgentHubBrands } from '~/consts/agentHub';

import useCurrentTenant from '~/hooks/useCurrentTenant';

import SearchService, { TypeaheadType } from '~/services/SearchService';

export type FlightLocationVariant = 'departure' | 'arrival';

interface Props {
  setCreatedConditions: (condition: RuleCondition, replace?: boolean) => void;
  existingConditions: RuleCondition['value'];
  variant: FlightLocationVariant;
}

const RELEVANT_TYPEAHEAD_TYPES: Array<TypeaheadType> = [
  'province_state',
  'neighborhood',
  'city',
  'high_level_region',
  'country',
  'multi_city_vicinity',
  'continent',
  'point_of_interest',
  'colloquial_area',
  'le_property',
  'le_property_unique_stays',
  'bedbank_property',
  'bedbank_property_unique_stays',
];

const LABELS: Record<FlightLocationVariant, string> = {
  departure: 'Departure location',
  arrival: 'Arrival location',
};

function areLocationsEqual(left: CommissionOption, right: CommissionOption) {
  if (left.length !== right.length) {
    return false;
  }
  return left.every((item, index) => {
    const other = right[index];
    return item?.value === other?.value && item?.label === other?.label;
  });
}

/**
 * Flight departure/arrival commission conditions. Same typeahead datasource as
 * `AgentHubCommissionsLocationInput`; serialized to `rules.flightDeparture` /
 * `rules.flightArrival` and `additionalInfo` in `CommissionDialog` / `buildRuleParam`.
 */
export default function AgentHubCommissionsFlightLocationInput({
  setCreatedConditions,
  existingConditions,
  variant,
}: Props) {
  const ruleType: RuleCondition['type'] = variant;
  const { tenant } = useCurrentTenant();
  const [loadingState, setLoadingState] = useState<Utils.FetchingState>('idle');
  const [result, setResult] = useState<CommissionOption>([]);
  const [selectedLocations, setSelectedLocations] = useState<CommissionOption>(existingConditions || []);
  const [noOptionsText, setNoOptionsText] = useState<string>('Start typing to search for locations');

  const debounceGetLocations = useMemo(
    () =>
      debounce((search: string) => {
        if (!search) {
          setResult([]);
          return;
        }
        setLoadingState('loading');
        SearchService.typeahead(search, {
          type: RELEVANT_TYPEAHEAD_TYPES,
          brand: ensureAgentHubBrands(tenant.brand),
          region: 'AU',
          limit: 10,
          vertical: 'hotel',
          priority: 'current',
        })
          .then((response) => {
            setResult(
              response.result.map((location) => ({
                value: location.format.mainText,
                label: location.format.mainText,
              })),
            );
            setLoadingState('success');
          })
          .catch(() => {
            setResult([]);
            setNoOptionsText('Failed to fetch location');
          });
      }, 300),
    [tenant.brand],
  );

  const handleSaveCondition = useCallback(() => {
    setCreatedConditions(
      {
        type: ruleType,
        value: selectedLocations,
      },
      true,
    );
  }, [ruleType, selectedLocations, setCreatedConditions]);

  useEffect(() => {
    const nextLocations = existingConditions || [];
    setSelectedLocations((currentLocations) => {
      if (areLocationsEqual(currentLocations, nextLocations)) {
        return currentLocations;
      }
      return nextLocations;
    });
  }, [variant, existingConditions]);

  useEffect(() => {
    handleSaveCondition();
  }, [handleSaveCondition]);

  const handleInputChange = (_: ChangeEvent, newInputValue: string) => {
    if (!newInputValue) {
      setNoOptionsText('Start typing to search for locations');
      return;
    }
    debounceGetLocations(newInputValue);
  };

  const handleChange = (_: ChangeEvent, value: CommissionOption) => {
    setSelectedLocations(value);
  };

  const label = LABELS[variant];

  return (
    <Stack spacing={2}>
      <Box>
        <Autocomplete
          multiple
          loading={loadingState === 'loading'}
          options={result}
          getOptionLabel={(option) => option.label}
          defaultValue={[]}
          filterSelectedOptions
          renderInput={(params) => (
            <TextField {...params} label={label} placeholder={selectedLocations.length > 0 ? '' : label} />
          )}
          onInputChange={handleInputChange}
          value={selectedLocations}
          onChange={handleChange}
          noOptionsText={noOptionsText}
          disablePortal
        />
        <Typography variant="caption" color="textSecondary">
          Start typing to search for a location
        </Typography>
      </Box>
    </Stack>
  );
}
